// Standalone shader strings for sharing with other developers
// Import these strings into any WebGL/Three.js project and wire up uniforms as needed.

export const vertexShader = `#version 300 es
in vec3 position;
in vec2 uv;
out vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position, 1.0);
}
`

export const fragmentShader = `#version 300 es
precision highp float;

in vec2 vUv;
out vec4 fragColor;
uniform vec2 u_resolution;
uniform vec2 u_mouse;
uniform float u_time;
uniform float u_timeScale;
uniform float u_lineSpacing;
uniform float u_lineThickness;
uniform float u_gradientTop;
uniform float u_dotsPerCell;
uniform float u_dotSize;
uniform float u_dotPaddingBoost;
uniform float u_dpr;

// Background gradient
uniform vec3 u_gradientColorTop;
uniform vec3 u_gradientColorBottom;
uniform float u_gradientAngle;
uniform int u_blendMode; // 0=normal, 1=screen, 2=multiply, 3=overlay, 4=add
uniform vec3 u_gridColor;

vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 permute(vec3 x) { return mod289(((x*34.0)+1.0)*x); }

float snoise(vec2 v) {
    const vec4 C = vec4(0.211324865405187,  // (3.0-sqrt(3.0))/6.0
                        0.366025403784439,  // 0.5*(sqrt(3.0)-1.0)
                        -0.577350269189626,  // -1.0 + 2.0 * C.x
                        0.024390243902439); // 1.0 / 41.0
    vec2 i  = floor(v + dot(v, C.yy) );
    vec2 x0 = v -   i + dot(i, C.xx);
    vec2 i1;
    i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
    vec4 x12 = x0.xyxy + C.xxzz;
    x12.xy -= i1;
    i = mod289(i); // Avoid truncation effects in permutation
    vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0 ))
        + i.x + vec3(0.0, i1.x, 1.0 ));

    vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
    m = m*m ;
    m = m*m ;
    vec3 x = 2.0 * fract(p * C.www) - 1.0;
    vec3 h = abs(x) - 0.5;
    vec3 ox = floor(x + 0.5);
    vec3 a0 = x - ox;
    m *= 1.79284291400159 - 0.85373472095314 * ( a0*a0 + h*h );
    vec3 g;
    g.x  = a0.x  * x0.x  + h.x  * x0.y;
    g.yz = a0.yz * x12.xz + h.yz * x12.yw;
    return 130.0 * dot(m, g);
}

// Shapes
float dotsMask(vec2 coord){
  // Place a grid of dots inside each main grid cell: dotsPerCell x dotsPerCell
  // Scale spacing by DPR so grid appears same size on all devices
  float spacing = u_lineSpacing * max(1.0, u_dpr); // main grid spacing
  float dotsPerCell = u_dotsPerCell; // use uniform for dots per cell
  float sub = spacing / dotsPerCell; // subcell size
  float halfSub = sub * 0.5;
  // Allow a padding boost which reduces dot size to increase padding around dots
  // Scale dot size by DPR so dots appear same size on all devices
  float dotSize = max(1.0, u_dotSize - u_dotPaddingBoost) * max(1.0, u_dpr);
  float halfDot = dotSize * 0.5;
  // position within main cell
  vec2 localMain = mod(coord, spacing);
  // position within subcell
  vec2 lc = mod(localMain, sub);
  vec2 d = abs(lc - vec2(halfSub)) - vec2(halfDot);
  float m = step(max(d.x, d.y), 0.0);
  return m;
}

float gridLines(vec2 coord, float spacing, float halfThick){
  float dx = abs(mod(coord.x + spacing * 0.5, spacing) - spacing * 0.5);
  float dy = abs(mod(coord.y + spacing * 0.5, spacing) - spacing * 0.5);
  float scaledHalf = halfThick * max(1.0, u_dpr);
  // anti-aliased lines using smoothstep around the thickness
  float aa = max(0.5, 1.0 * max(1.0, u_dpr));
  float v = smoothstep(scaledHalf + aa, scaledHalf - aa, dx);
  float h = smoothstep(scaledHalf + aa, scaledHalf - aa, dy);
  return max(v, h);
}

float crosses(vec2 coord, float spacing, float halfSize, float halfThick){
  vec2 lp;
  lp.x = mod(coord.x + spacing * 0.5, spacing) - spacing * 0.5;
  lp.y = mod(coord.y + spacing * 0.5, spacing) - spacing * 0.5;
  float scaledHalfThick = halfThick * max(1.0, u_dpr);
  float crossH = step(abs(lp.y), scaledHalfThick) * step(abs(lp.x), halfSize);
  float crossV = step(abs(lp.x), scaledHalfThick) * step(abs(lp.y), halfSize);
  return max(crossH, crossV);
}

void main() {
    // Keep square sampling to avoid stretching: center a square region
    float minRes = min(u_resolution.x, u_resolution.y);
    vec2 pixel = gl_FragCoord.xy;
    vec2 offset = vec2((u_resolution.x - minRes) * 0.5, (u_resolution.y - minRes) * 0.5);
    vec2 coord = pixel - offset; // in px within square

    // Scale spacing by DPR so grid appears same size on all devices
    float scaledSpacing = u_lineSpacing * max(1.0, u_dpr);

    // Shapes computed in pixel space (square region)
    float dots = dotsMask(coord);
    float lines = gridLines(coord, scaledSpacing, u_lineThickness * 0.5);
    // Crosses centered at intersections; make thickness match lines (use same half-thickness)
    float cross = crosses(coord, scaledSpacing, 5.0 * max(1.0, u_dpr), u_lineThickness * 0.5); // halfSize scaled for DPR
    // Make grid lines semi-opaque (70%) while dots and crosses stay full strength
    float linesAlpha = lines * 0.7;
    float shape = max(dots, max(linesAlpha, cross));

    // Noise using provided snoise implementation
    vec2 st = gl_FragCoord.xy / u_resolution.xy;
    st.x *= u_resolution.x / u_resolution.y; // correct aspect
    vec2 pos = st * 3.0;

    float DF = 0.0;
    float a = 0.0;
    vec2 vel = vec2(u_time * 0.1 * u_timeScale);
    DF += snoise(pos + vel) * 0.25 + 0.25;

    a = snoise(pos * vec2(cos(u_time * 0.15 * u_timeScale), sin(u_time * 0.1 * u_timeScale)) * 0.1) * 3.1415;
    vel = vec2(cos(a), sin(a));
    DF += snoise(pos + vel) * 0.25 + 0.25;

    float f = fract(DF);

    // Binary-like noise mask with rough edges but softened slightly to avoid extreme harshness
    // Use lower-frequency edge noise and a narrow smoothstep around threshold
    float edgeNoise = snoise(pos * 4.0 + vec2(u_time * 0.5));
    float threshold = 0.6 + edgeNoise * 0.08; // smaller perturbation
    float edgeWidth = 0.05; // narrow transition
    float noiseMask = smoothstep(threshold - edgeWidth, threshold + edgeWidth, f);
    // Push to more binary but keep rough boundary: sharpen a bit
    noiseMask = pow(noiseMask, 1.4);

    // Top 40% mask control: linear gradient reaching full at the top over the specified fraction
    float grad = smoothstep(1.0 - u_gradientTop, 1.0, vUv.y);

    float gridAlpha = shape * noiseMask * grad;
    vec3 gridColor = u_gridColor;

    // Background gradient with angle support
    float angle = u_gradientAngle * 3.14159265 / 180.0;
    vec2 gradDir = vec2(cos(angle), sin(angle));
    float gradT = dot(vUv - 0.5, gradDir) + 0.5;
    gradT = clamp(gradT, 0.0, 1.0);
    vec3 bgColor = mix(u_gradientColorBottom, u_gradientColorTop, gradT);

    // Blend modes
    vec3 blended;
    if (u_blendMode == 0) {
        // Normal: grid over background
        blended = mix(bgColor, gridColor, gridAlpha);
    } else if (u_blendMode == 1) {
        // Screen
        vec3 screened = 1.0 - (1.0 - bgColor) * (1.0 - gridColor);
        blended = mix(bgColor, screened, gridAlpha);
    } else if (u_blendMode == 2) {
        // Multiply
        vec3 multiplied = bgColor * gridColor;
        blended = mix(bgColor, multiplied, gridAlpha);
    } else if (u_blendMode == 3) {
        // Overlay
        vec3 overlayed = mix(
            2.0 * bgColor * gridColor,
            1.0 - 2.0 * (1.0 - bgColor) * (1.0 - gridColor),
            step(0.5, bgColor)
        );
        blended = mix(bgColor, overlayed, gridAlpha);
    } else if (u_blendMode == 4) {
        // Additive
        blended = bgColor + gridColor * gridAlpha;
    } else {
        blended = mix(bgColor, gridColor, gridAlpha);
    }

    // Output with full opacity when gradient colors are set, transparent otherwise
    float hasGradient = step(0.001, length(u_gradientColorTop) + length(u_gradientColorBottom));
    float outAlpha = mix(gridAlpha, 1.0, hasGradient);
    fragColor = vec4(blended, outAlpha);
}
`

export const defaultUniforms = {
  u_resolution: { value: [1, 1] },
  u_mouse: { value: [0, 0] },
  u_time: { value: 0 },
  u_timeScale: { value: 3.0 },
  u_lineSpacing: { value: 60 },
  u_dotsPerCell: { value: 6 },
  u_dotSize: { value: 2.0 },
  u_dotPaddingBoost: { value: 1.0 },
  u_lineThickness: { value: 1.0 },
  u_gradientTop: { value: 0.4 },
  u_dpr: { value: 1.0 },
  // Background gradient
  u_gradientColorTop: { value: [0, 0, 0] },
  u_gradientColorBottom: { value: [0, 0, 0] },
  u_gradientAngle: { value: 90 }, // 90 = bottom to top
  u_blendMode: { value: 0 }, // 0=normal, 1=screen, 2=multiply, 3=overlay, 4=add
  u_gridColor: { value: [1, 1, 1] }, // white by default
}

export type BlendMode = 'normal' | 'screen' | 'multiply' | 'overlay' | 'add'

export const blendModeToInt: Record<BlendMode, number> = {
  normal: 0,
  screen: 1,
  multiply: 2,
  overlay: 3,
  add: 4,
}
